import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Skeleton,
  Stack,
  Typography,
  keyframes
} from '@mui/material';
import {
  AccessTimeOutlined,
  ArrowForward,
  CalendarMonthOutlined,
  CheckCircleOutline,
  ChevronRight,
  DirectionsRun,
  EmojiEventsOutlined,
  FitnessCenter,
  GroupsOutlined,
  LaunchOutlined,
  PersonAddOutlined,
  PlaceOutlined,
  CampaignOutlined,
  Pool,
  PublicOutlined,
  RocketLaunchOutlined,
  SportsBaseball,
  SportsBasketball,
  SportsGolf,
  SportsRugby,
  SportsSoccer,
  SportsTennis,
  SportsVolleyball,
  Star
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { PublicHomeEvent } from '../types/Event';
import screen1 from '../assets/screen1.png';
import screen2 from '../assets/screen2.png';
import screen3 from '../assets/screen3.png';

const ICON_MAP: { [key: string]: React.ElementType } = {
  SportsTennis,
  SportsBasketball,
  SportsSoccer,
  SportsVolleyball,
  SportsBaseball,
  SportsRugby,
  SportsGolf,
  DirectionsRun,
  FitnessCenter,
  Pool
};

const DEFAULT_HERO_BACKGROUND = 'linear-gradient(135deg, #0a0e27 0%, #1a1f4e 55%, #2d35a1 100%)';

const isLightBackground = (background: string): boolean => {
  let isLight = false;
  if (background.startsWith('#')) {
    const hex = background.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    isLight = (r * 299 + g * 587 + b * 114) / 1000 > 128;
  } else if (background.includes('linear-gradient')) {
    const match = background.match(/#[a-fA-F0-9]{6}/);
    if (match) {
      const hex = match[0].replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      isLight = (r * 299 + g * 587 + b * 114) / 1000 > 150;
    }
  }
  return isLight;
};

const accent = '#5cb8ff';
const accentHover = '#7ec8ff';
const cyan = '#a0d8ff';
const deepNavy = '#060a1e';

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(3deg); }
`;

const floatSlow = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-12px) rotate(-2deg); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const auroraDrift = keyframes`
  0% { transform: translate(0%, 0%) rotate(0deg) scale(1); }
  33% { transform: translate(2%, -4%) rotate(1deg) scale(1.04); }
  66% { transform: translate(-1%, 2%) rotate(-0.5deg) scale(0.97); }
  100% { transform: translate(0%, 0%) rotate(0deg) scale(1); }
`;

const auroraDrift2 = keyframes`
  0% { transform: translate(0%, 0%) rotate(0deg) scale(1); }
  33% { transform: translate(-3%, 3%) rotate(-1deg) scale(1.03); }
  66% { transform: translate(2%, -1%) rotate(0.8deg) scale(0.98); }
  100% { transform: translate(0%, 0%) rotate(0deg) scale(1); }
`;

const auroraDrift3 = keyframes`
  0% { transform: translate(0%, 0%) scale(1); }
  50% { transform: translate(1%, -3%) scale(1.06); }
  100% { transform: translate(0%, 0%) scale(1); }
`;

const formatEventDate = (date: string, timestamp: string) => {
  const parsedDate = timestamp ? new Date(timestamp) : new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) return date;
  return new Intl.DateTimeFormat('zh-HK', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  }).format(parsedDate);
};

const formatPrice = (event: PublicHomeEvent) => {
  if (event.splitFee) {
    return event.totalCost ? `平分費用，HK$${event.totalCost} 起` : '平分費用';
  }
  if (event.memberFee !== undefined && event.normalFee !== undefined && event.memberFee !== event.normalFee) {
    return `會員 HK$${event.memberFee} · 非會員 HK$${event.normalFee}`;
  }
  if (event.memberFee !== undefined || event.normalFee !== undefined) {
    return `費用 HK$${event.memberFee ?? event.normalFee} 起`;
  }
  return '查看活動詳情';
};

const getSiteIcon = (iconName?: string) => ICON_MAP[iconName || 'SportsTennis'] || SportsTennis;

const WALKTHROUGH_STEPS = [
  {
    step: 1,
    icon: <PersonAddOutlined />,
    title: '註冊帳戶',
    description: '以 WhatsApp 驗證碼快速建立帳戶，無需額外填寫繁複資料。',
    gradient: 'linear-gradient(135deg, rgba(92,184,255,0.15) 0%, rgba(92,184,255,0.02) 100%)',
    iconColor: accent,
  },
  {
    step: 2,
    icon: <GroupsOutlined />,
    title: '建立體育群組',
    description: '為你的課堂、球隊或恆常約局建立專屬群組頁面，方便統一管理。',
    gradient: 'linear-gradient(135deg, rgba(160,216,255,0.15) 0%, rgba(160,216,255,0.02) 100%)',
    iconColor: cyan,
  },
  {
    step: 3,
    icon: <SportsTennis />,
    title: '創建你的活動',
    description: '設定日期、時間、地點及名額，一鍵建立活動並通知群組成員報名。',
    gradient: 'linear-gradient(135deg, rgba(92,184,255,0.1) 0%, rgba(160,216,255,0.1) 100%)',
    iconColor: accent,
  },
];

const PARTICIPANT_BENEFITS = [
  {
    icon: <CalendarMonthOutlined />,
    title: '一鍵報名加入',
    description: '看到心儀活動，點擊即可填寫資料報名。支援候補名單，名額滿了也不怕錯過。',
    gradient: 'linear-gradient(135deg, rgba(92,184,255,0.15) 0%, rgba(92,184,255,0.02) 100%)'
  },
  {
    icon: <EmojiEventsOutlined />,
    title: '即時狀態追蹤',
    description: '報名後可即時查看狀態：待確認、待付款、已付款、已報到，流程清晰透明。',
    gradient: 'linear-gradient(135deg, rgba(160,216,255,0.15) 0%, rgba(160,216,255,0.02) 100%)'
  },
  {
    icon: <CampaignOutlined />,
    title: '接收活動公告',
    description: '群組管理員發佈的重要公告和最新安排，即時推送到你的裝置，不會錯過任何更新。',
    gradient: 'linear-gradient(135deg, rgba(92,184,255,0.1) 0%, rgba(160,216,255,0.1) 100%)'
  },
  {
    icon: <AccessTimeOutlined />,
    title: '個人化活動總覽',
    description: '登入後即可在「我的活動」頁面查看所有已報名活動，支援日曆匯出，方便安排日程。',
    gradient: 'linear-gradient(135deg, rgba(92,184,255,0.12) 0%, rgba(92,184,255,0.02) 100%)'
  },
];

const ADMIN_BENEFITS = [
  {
    icon: <RocketLaunchOutlined />,
    title: '建立活動與公開發佈',
    description: '快速建立體育活動並設定為公開，讓更多球友透過首頁發現你的群組和活動。',
    gradient: 'linear-gradient(135deg, rgba(92,184,255,0.15) 0%, rgba(92,184,255,0.02) 100%)'
  },
  {
    icon: <GroupsOutlined />,
    title: '統一管理報名與審批',
    description: '一目了然查看報名狀況，支援審批機制、候補名單和付款追蹤，管理球局更有效率。',
    gradient: 'linear-gradient(135deg, rgba(160,216,255,0.15) 0%, rgba(160,216,255,0.02) 100%)'
  },
  {
    icon: <PublicOutlined />,
    title: '公告與 WhatsApp 提醒',
    description: '透過平台發佈活動公告，並可一鍵發送 WhatsApp 提醒，確保每位參加者都收到最新資訊。',
    gradient: 'linear-gradient(135deg, rgba(92,184,255,0.1) 0%, rgba(160,216,255,0.1) 100%)'
  },
];

const GuestHome: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<PublicHomeEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = [screen1, screen2, screen3];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [images.length]);

  useEffect(() => {
    let isMounted = true;
    const fetchHomepageEvents = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/public/home/events?limit=6`);
        if (isMounted) {
          setEvents(response.data);
          setError(null);
        }
      } catch (err) {
        console.error('Error loading homepage events:', err);
        if (isMounted) setError('暫時未能載入公開活動，請稍後再試。');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchHomepageEvents();
    return () => { isMounted = false; };
  }, []);

  const scrollToEvents = () => {
    document.getElementById('public-events')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box sx={{ bgcolor: deepNavy, minHeight: '100vh', overflow: 'hidden' }}>
      {/* ===== HERO SECTION ===== */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '100vh', md: '92vh' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          background: `radial-gradient(ellipse 120% 80% at 50% 120%, rgba(6, 30, 80, 0.8) 0%, transparent 60%), ${deepNavy}`
        }}
      >
        {/* Aurora mesh layer 1 — main blue bloom */}
        <Box sx={{
          position: 'absolute',
          top: '-20%', left: '-10%',
          width: '80%', height: '80%',
          background: `radial-gradient(ellipse 70% 50% at 60% 50%, rgba(92,184,255,0.12) 0%, rgba(40,100,200,0.06) 40%, transparent 70%)`,
          filter: 'blur(60px)',
          animation: `${auroraDrift} 20s ease-in-out infinite`,
          zIndex: 0
        }} />
        {/* Aurora mesh layer 2 — cyan accent */}
        <Box sx={{
          position: 'absolute',
          top: '10%', right: '-15%',
          width: '70%', height: '70%',
          background: `radial-gradient(ellipse 50% 60% at 40% 40%, rgba(160,216,255,0.08) 0%, rgba(80,160,255,0.04) 45%, transparent 70%)`,
          filter: 'blur(80px)',
          animation: `${auroraDrift2} 25s ease-in-out infinite`,
          zIndex: 0
        }} />
        {/* Aurora mesh layer 3 — deep violet undertone */}
        <Box sx={{
          position: 'absolute',
          bottom: '-10%', left: '10%',
          width: '60%', height: '50%',
          background: `radial-gradient(ellipse 80% 60% at 50% 60%, rgba(100,60,200,0.06) 0%, rgba(60,40,160,0.03) 40%, transparent 65%)`,
          filter: 'blur(70px)',
          animation: `${auroraDrift3} 18s ease-in-out infinite`,
          zIndex: 0
        }} />
        {/* Bright focal glow behind hero text area */}
        <Box sx={{
          position: 'absolute',
          top: '30%', left: '5%',
          width: '45%', height: '40%',
          background: `radial-gradient(ellipse 100% 100% at 50% 50%, rgba(92,184,255,0.05) 0%, transparent 70%)`,
          filter: 'blur(40px)',
          zIndex: 0
        }} />

        {/* Noise texture overlay for depth */}
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 0, opacity: 0.025, pointerEvents: 'none',
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
          backgroundSize: '200px 200px'
        }} />

        {/* Floating sport-themed decorative elements */}
        <Box sx={{ position: 'absolute', top: '5%', right: '8%', animation: `${float} 9s ease-in-out infinite`, zIndex: 0, opacity: 0.05, transform: 'rotate(-15deg)' }}>
          <SportsBasketball sx={{ fontSize: 140, color: accent }} />
        </Box>
        <Box sx={{ position: 'absolute', bottom: '12%', left: '4%', animation: `${float} 11s ease-in-out infinite 1.5s`, zIndex: 0, opacity: 0.04, transform: 'rotate(20deg)' }}>
          <SportsVolleyball sx={{ fontSize: 100, color: cyan }} />
        </Box>
        <Box sx={{ position: 'absolute', top: '50%', right: '18%', animation: `${floatSlow} 8s ease-in-out infinite 0.5s`, zIndex: 0, opacity: 0.04, transform: 'rotate(25deg)' }}>
          <SportsTennis sx={{ fontSize: 80, color: cyan }} />
        </Box>
        <Box sx={{ position: 'absolute', top: '22%', right: '28%', animation: `${floatSlow} 7s ease-in-out infinite 2s`, zIndex: 0, opacity: 0.035, transform: 'rotate(-10deg)' }}>
          <SportsSoccer sx={{ fontSize: 60, color: cyan }} />
        </Box>
        <Box sx={{ position: 'absolute', top: '70%', left: '15%', animation: `${float} 10s ease-in-out infinite 3s`, zIndex: 0, opacity: 0.04, transform: 'rotate(45deg)' }}>
          <SportsBaseball sx={{ fontSize: 75, color: accent }} />
        </Box>
        <Box sx={{ position: 'absolute', top: '15%', left: '30%', animation: `${floatSlow} 12s ease-in-out infinite 1s`, zIndex: 0, opacity: 0.03, transform: 'rotate(-30deg)' }}>
          <SportsGolf sx={{ fontSize: 90, color: cyan }} />
        </Box>
        <Box sx={{ position: 'absolute', bottom: '25%', right: '35%', animation: `${float} 8s ease-in-out infinite 4s`, zIndex: 0, opacity: 0.035, transform: 'rotate(15deg)' }}>
          <SportsRugby sx={{ fontSize: 110, color: accent }} />
        </Box>
        <Box sx={{ position: 'absolute', top: '40%', left: '45%', animation: `${floatSlow} 9s ease-in-out infinite 2.5s`, zIndex: 0, opacity: 0.02, transform: 'rotate(-20deg)' }}>
          <Pool sx={{ fontSize: 65, color: cyan }} />
        </Box>
        <Box sx={{ position: 'absolute', bottom: '5%', right: '50%', animation: `${float} 11s ease-in-out infinite 0.8s`, zIndex: 0, opacity: 0.03, transform: 'rotate(10deg)' }}>
          <FitnessCenter sx={{ fontSize: 85, color: accent }} />
        </Box>

        {/* Soft vignette */}
        <Box sx={{
          position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
          background: `radial-gradient(ellipse 90% 80% at 50% 50%, transparent 40%, ${deepNavy} 100%)`
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 12, md: 0 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 6, md: 10 }} alignItems="center">
            {/* Left: Hero text */}
            <Box sx={{ flex: 1, maxWidth: 700 }}>
              <Chip
                label="MATCHPOINT — 體育活動管理平台"
                sx={{
                  mb: 4,
                  color: accent,
                  bgcolor: 'rgba(92,184,255,0.08)',
                  border: '1px solid rgba(92,184,255,0.2)',
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  fontSize: '0.7rem',
                  animation: `${slideUp} 0.6s ease-out`
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.6rem', sm: '3.2rem', md: '4.6rem' },
                  lineHeight: 1.05,
                  fontWeight: 900,
                  letterSpacing: '-0.04em',
                  mb: 3,
                  color: 'white',
                  animation: `${slideUp} 0.6s ease-out 0.1s both`
                }}
              >
                <Box component="span" sx={{
                  background: `linear-gradient(135deg, ${accent} 0%, #a0d8ff 50%, ${cyan} 100%)`,
                  backgroundSize: '200% 100%',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  animation: `${shimmer} 4s linear infinite`
                }}>
                  MatchPoint
                </Box>
                <br />
                球場見
              </Typography>
              <Stack
                spacing={2}
                sx={{
                  maxWidth: 520,
                  mb: 5,
                  animation: `${slideUp} 0.6s ease-out 0.2s both`
                }}
              >
                {[
                  '一站式瀏覽各體育群組公開活動',
                  '隨時查看時間、地點及名額',
                  '群組管理員亦可統一管理報名、公告與提醒，方便又清晰'
                ].map((text, i) => (
                  <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                    <CheckCircleOutline sx={{ color: accent, fontSize: '1.25rem', opacity: 0.8 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'rgba(255,255,255,0.65)',
                        fontWeight: 400,
                        fontSize: { xs: '1rem', md: '1.15rem' },
                        lineHeight: 1.5,
                      }}
                    >
                      {text}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ animation: `${slideUp} 0.6s ease-out 0.3s both` }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={scrollToEvents}
                  endIcon={<ArrowForward />}
                  sx={{
                    px: 4,
                    py: 1.6,
                    borderRadius: '14px',
                    bgcolor: accent,
                    color: deepNavy,
                    fontWeight: 800,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    boxShadow: `0 0 30px rgba(92,184,255,0.25)`,
                    '&:hover': { bgcolor: '#7ec8ff', boxShadow: `0 0 40px rgba(92,184,255,0.4)` }
                  }}
                >
                  查看公開活動
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/signup')}
                  sx={{
                    px: 4,
                    py: 1.6,
                    borderRadius: '14px',
                    color: 'rgba(255,255,255,0.8)',
                    borderColor: 'rgba(255,255,255,0.15)',
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    '&:hover': {
                      borderColor: accent,
                      color: accent,
                      bgcolor: 'rgba(92,184,255,0.04)'
                    }
                  }}
                >
                  建立你的體育群組
                </Button>
              </Stack>
            </Box>

            {/* Right: Phone mockup */}
            <Box
              sx={{
                width: '100%',
                maxWidth: 280,
                display: 'block',
                position: 'relative',
                animation: `${slideUp} 0.8s ease-out 0.3s both, ${floatSlow} 6s ease-in-out 1.5s infinite`
              }}
            >
              {images.map((img, index) => (
                <Box
                  key={index}
                  component="img"
                  src={img}
                  alt={`App preview ${index + 1}`}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    position: index === 0 ? 'relative' : 'absolute',
                    top: 0,
                    left: 0,
                    opacity: currentImageIndex === index ? 1 : 0,
                    transition: 'opacity 1s ease-in-out',
                    filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.5))'
                  }}
                />
              ))}
            </Box>
          </Stack>
        </Container>

        {/* Diagonal bottom cut */}
        <Box sx={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: '160px', zIndex: 2,
          background: `linear-gradient(to bottom, transparent 0%, rgba(6,10,30,0.6) 50%, ${deepNavy} 100%)`
        }} />
      </Box>

      {/* ===== EVENTS SECTION ===== */}
      <Box sx={{ py: { xs: 6, md: 10 }, position: 'relative' }}>
        <Container maxWidth="lg">
          <Box id="public-events" sx={{ mb: 5 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Box sx={{ width: 40, height: 3, borderRadius: 2, bgcolor: accent }} />
              <Typography variant="overline" sx={{ color: accent, fontWeight: 700, letterSpacing: '0.14em', fontSize: '0.7rem' }}>
                Public Events
              </Typography>
            </Stack>
            <Typography variant="h3" sx={{ fontWeight: 900, color: 'white', letterSpacing: '-0.03em', mb: 1 }}>
              精選體育活動
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.4)', maxWidth: 640, lineHeight: 1.8 }}>
              以下活動均由群組管理員設定為公開顯示<br />你可以先查看詳情<br />再進入對應群組報名參加
            </Typography>
          </Box>

          {error && (
            <Alert severity="warning" sx={{ mb: 3, bgcolor: 'rgba(255,180,0,0.08)', color: '#ffb400', border: '1px solid rgba(255,180,0,0.2)', '& .MuiAlert-icon': { color: '#ffb400' } }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
                gap: 3
              }}
            >
              {[1, 2, 3].map((item) => (
                <Paper key={item} elevation={0} sx={{
                  p: 0, overflow: 'hidden', borderRadius: '24px',
                  bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)'
                }}>
                  <Skeleton variant="rectangular" height={130} sx={{ bgcolor: 'rgba(255,255,255,0.04)' }} />
                  <Box sx={{ p: 3 }}>
                    <Skeleton variant="text" height={32} sx={{ bgcolor: 'rgba(255,255,255,0.04)' }} />
                    <Skeleton variant="text" sx={{ bgcolor: 'rgba(255,255,255,0.04)' }} />
                    <Skeleton variant="text" width="80%" sx={{ bgcolor: 'rgba(255,255,255,0.04)' }} />
                    <Skeleton variant="rectangular" height={44} sx={{ mt: 3, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.04)' }} />
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : events.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 5, md: 7 },
                textAlign: 'center',
                borderRadius: '28px',
                border: '1px dashed rgba(92,184,255,0.15)',
                bgcolor: 'rgba(92,184,255,0.02)'
              }}
            >
              <Box sx={{ width: 80, height: 80, borderRadius: '24px', bgcolor: 'rgba(92,184,255,0.08)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                <EmojiEventsOutlined sx={{ fontSize: 40, color: accent }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'white', mb: 1 }}>
                暫時未有公開活動
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.4)', mb: 4, maxWidth: 480, mx: 'auto', lineHeight: 1.8 }}>
                當群組管理員把體育活動設為公開後<br />相關活動便會自動顯示在這裡
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/signup')}
                endIcon={<ChevronRight />}
                sx={{
                  px: 4, py: 1.4, borderRadius: '14px',
                  bgcolor: accent, color: deepNavy, fontWeight: 800,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#7ec8ff' }
                }}
              >
                建立你的體育群組
              </Button>
            </Paper>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
                gap: 3
              }}
            >
              {events.map((event) => {
                const SiteIcon = getSiteIcon(event.site.icon);
                const headerBg = event.site.heroBackground || DEFAULT_HERO_BACKGROUND;
                const light = isLightBackground(headerBg);
                return (
                  <Paper
                    key={event._id}
                    elevation={0}
                    sx={{
                      overflow: 'hidden',
                      borderRadius: '24px',
                      border: '1px solid rgba(255,255,255,0.06)',
                      bgcolor: 'rgba(255,255,255,0.02)',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        borderColor: 'rgba(92,184,255,0.2)',
                        transform: 'translateY(-4px)',
                        boxShadow: `0 24px 60px rgba(0,0,0,0.3), 0 0 40px rgba(92,184,255,0.04)`
                      }
                    }}
                  >
                    {/* Event card header */}
                    <Box
                      sx={{
                        p: 3,
                        background: headerBg,
                        color: light ? 'text.primary' : 'white',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: -30, right: -30,
                          width: 100, height: 100,
                          borderRadius: '50%',
                          bgcolor: light ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)'
                        }
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ position: 'relative', zIndex: 1 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{ bgcolor: light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.15)', width: 48, height: 48, border: `2px solid ${light ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.1)'}` }}>
                            <SiteIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="overline" sx={{ opacity: 0.7, letterSpacing: '0.12em', fontSize: '0.65rem' }}>
                              {formatEventDate(event.date, event.timestamp)}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, textShadow: light ? 'none' : '0 1px 2px rgba(0,0,0,0.3)' }}>
                              {event.site.name}
                            </Typography>
                          </Box>
                        </Stack>
                        <Chip
                          label={event.type === 'tournament' ? '比賽' : '公開'}
                          size="small"
                          sx={{
                            bgcolor: event.type === 'tournament' ? (light ? 'rgba(25,118,210,0.15)' : 'rgba(92,184,255,0.2)') : (light ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.12)'),
                            color: event.type === 'tournament' ? (light ? '#1565c0' : accent) : (light ? 'text.primary' : 'white'),
                            border: `1px solid ${event.type === 'tournament' ? (light ? 'rgba(25,118,210,0.2)' : 'rgba(92,184,255,0.3)') : (light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)')}`,
                            fontWeight: 700,
                            fontSize: '0.7rem'
                          }}
                        />
                      </Stack>
                    </Box>

                    {/* Event card body */}
                    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1 }}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip size="small" icon={<AccessTimeOutlined sx={{ color: `${accent} !important` }} />} label={event.time} sx={{ bgcolor: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.06)' }} />
                        <Chip size="small" icon={<GroupsOutlined sx={{ color: `${cyan} !important` }} />} label={`尚餘 ${event.availableSpots} 個名額`} sx={{ bgcolor: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.06)' }} />
                        <Chip size="small" label={formatPrice(event)} sx={{ bgcolor: 'rgba(92,184,255,0.06)', color: accent, border: '1px solid rgba(92,184,255,0.12)', fontWeight: 600 }} />
                      </Stack>

                      <Stack spacing={1.2}>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <PlaceOutlined sx={{ fontSize: 16, color: 'rgba(255,255,255,0.3)' }} />
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            {event.location}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <CalendarMonthOutlined sx={{ fontSize: 16, color: 'rgba(255,255,255,0.3)' }} />
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                            已報名 {event.participantsCount} / {event.maxParticipants}
                          </Typography>
                        </Stack>
                      </Stack>

                      {/* Progress bar for spots */}
                      <Box sx={{ width: '100%', height: 3, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                        <Box sx={{
                          width: `${Math.min((event.participantsCount / event.maxParticipants) * 100, 100)}%`,
                          height: '100%',
                          borderRadius: 2,
                          background: `linear-gradient(90deg, ${accent}, ${cyan})`,
                          transition: 'width 0.6s ease'
                        }} />
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255,255,255,0.4)',
                          minHeight: 54,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.7
                        }}
                      >
                        {event.otherDetails || '打開活動即可查看完整內容、最新安排及報名方式。'}
                      </Typography>

                      <Stack direction="row" spacing={1.5} sx={{ mt: 'auto' }}>
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => navigate(`/${event.site.route}/event/${event._id}`)}
                          endIcon={<LaunchOutlined />}
                          sx={{
                            borderRadius: '12px',
                            bgcolor: accent,
                            color: deepNavy,
                            fontWeight: 800,
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#7ec8ff' }
                          }}
                        >
                          查看活動
                        </Button>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={() => navigate(`/${event.site.route}`)}
                          sx={{
                            borderRadius: '12px',
                            color: 'rgba(255,255,255,0.6)',
                            borderColor: 'rgba(255,255,255,0.1)',
                            fontWeight: 700,
                            textTransform: 'none',
                            '&:hover': {
                              borderColor: cyan,
                              color: cyan,
                              bgcolor: 'rgba(160,216,255,0.04)'
                            }
                          }}
                        >
                          前往群組
                        </Button>
                      </Stack>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Container>
      </Box>


      {/* ===== PARTICIPANT BENEFITS SECTION ===== */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Chip
              label="參加者"
              sx={{
                mb: 3,
                color: cyan,
                bgcolor: 'rgba(160,216,255,0.06)',
                border: '1px solid rgba(160,216,255,0.15)',
                fontWeight: 600,
                fontSize: '0.75rem',
                animation: `${slideUp} 0.6s ease-out`
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                color: 'white',
                letterSpacing: '-0.03em',
                mb: 2,
                animation: `${slideUp} 0.6s ease-out 0.1s both`
              }}
            >
              報名活動<br/>
              <Box component="span" sx={{ color: accent }}> 輕鬆簡單</Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.4)',
                maxWidth: 560,
                mx: 'auto',
                lineHeight: 1.8,
                animation: `${slideUp} 0.6s ease-out 0.15s both`
              }}
            >
              無需繁複註冊程序，一鍵即可報名參加心儀活動。隨時查看報名狀態及接收最新公告，讓你專注享受運動樂趣。
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 3
            }}
          >
            {PARTICIPANT_BENEFITS.map((benefit, i) => (
              <Paper
                key={i}
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: '24px',
                  height: '100%',
                  bgcolor: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: `${slideUp} 0.6s ease-out ${0.2 + i * 0.1}s both`,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderColor: 'rgba(92,184,255,0.2)',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 60px rgba(92,184,255,0.06)'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '16px',
                    background: benefit.gradient,
                    color: i % 2 === 1 ? cyan : accent,
                    mb: 3,
                    fontSize: '1.5rem'
                  }}
                >
                  {benefit.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'white', mb: 1.5, letterSpacing: '-0.01em' }}>
                  {benefit.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', lineHeight: 1.8 }}>
                  {benefit.description}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ===== ADMIN BENEFITS SECTION ===== */}
      <Box sx={{
        py: { xs: 8, md: 12 },
        bgcolor: 'rgba(92,184,255,0.02)',
        borderTop: '1px solid rgba(92,184,255,0.06)',
        borderBottom: '1px solid rgba(92,184,255,0.06)'
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Chip
              label="群組管理員"
              sx={{
                mb: 3,
                color: cyan,
                bgcolor: 'rgba(160,216,255,0.06)',
                border: '1px solid rgba(160,216,255,0.15)',
                fontWeight: 600,
                fontSize: '0.75rem',
                animation: `${slideUp} 0.6s ease-out`
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                color: 'white',
                letterSpacing: '-0.03em',
                mb: 2,
                animation: `${slideUp} 0.6s ease-out 0.1s both`
              }}
            >
              建立群組<br/>
              <Box component="span" sx={{ color: accent }}> 輕鬆管理</Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.4)',
                maxWidth: 560,
                mx: 'auto',
                lineHeight: 1.8,
                animation: `${slideUp} 0.6s ease-out 0.15s both`
              }}
            >
              一站式管理你的體育活動，從公開招募、審核報名到自動發送 WhatsApp 提醒，助你輕鬆帶領群組。
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              gap: 3
            }}
          >
            {ADMIN_BENEFITS.map((benefit, i) => (
              <Paper
                key={i}
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: '24px',
                  height: '100%',
                  bgcolor: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  animation: `${slideUp} 0.6s ease-out ${0.2 + i * 0.1}s both`,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderColor: 'rgba(92,184,255,0.2)',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 20px 60px rgba(92,184,255,0.06)'
                  }
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '16px',
                    background: benefit.gradient,
                    color: accent,
                    mb: 3,
                    fontSize: '1.5rem'
                  }}
                >
                  {benefit.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: 'white', mb: 1.5, letterSpacing: '-0.01em' }}>
                  {benefit.title}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', lineHeight: 1.8 }}>
                  {benefit.description}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ===== GUIDED WALKTHROUGH SECTION ===== */}
      <Box sx={{ py: { xs: 8, md: 12 }, position: 'relative' }}>
        <Container maxWidth="lg">
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Chip
              label="簡單三步"
              sx={{
                mb: 3,
                color: cyan,
                bgcolor: 'rgba(160,216,255,0.06)',
                border: '1px solid rgba(160,216,255,0.15)',
                fontWeight: 600,
                fontSize: '0.75rem',
                animation: `${slideUp} 0.6s ease-out`
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                color: 'white',
                letterSpacing: '-0.03em',
                mb: 2,
                animation: `${slideUp} 0.6s ease-out 0.1s both`
              }}
            >
              由註冊到舉辦<br/>
              <Box component="span" sx={{ color: accent }}> 只需幾分鐘</Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.4)',
                maxWidth: 560,
                mx: 'auto',
                lineHeight: 1.8,
                animation: `${slideUp} 0.6s ease-out 0.15s both`
              }}
            >
              跟著以下三個簡單步驟<br />立即開始管理你的體育群組和活動
            </Typography>
          </Box>

          {/* Steps with connectors */}
          <Box sx={{ position: 'relative' }}>
            {/* Desktop horizontal connector line */}
            <Box sx={{
              display: { xs: 'none', md: 'block' },
              position: 'absolute',
              top: 32,
              left: 'calc(16.67% + 32px)',
              right: 'calc(16.67% + 32px)',
              height: '2px',
              background: 'linear-gradient(90deg, rgba(92,184,255,0.25), rgba(160,216,255,0.25), rgba(92,184,255,0.25))',
              zIndex: 0,
            }} />

            {/* Mobile vertical connector line */}
            <Box sx={{
              display: { xs: 'block', md: 'none' },
              position: 'absolute',
              left: 'calc(50% - 1px)',
              top: '32px',
              bottom: '32px',
              width: '2px',
              background: 'linear-gradient(180deg, rgba(92,184,255,0.25), rgba(160,216,255,0.25))',
              zIndex: 0,
            }} />

            {/* Step cards grid */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              gap: 3,
              position: 'relative',
              zIndex: 1,
            }}>
              {WALKTHROUGH_STEPS.map((step, i) => (
                <Paper
                  key={step.step}
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: '24px',
                    height: '100%',
                    bgcolor: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    textAlign: 'center',
                    animation: `${slideUp} 0.6s ease-out ${0.2 + i * 0.1}s both`,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.05)',
                      borderColor: 'rgba(92,184,255,0.2)',
                      transform: 'translateY(-4px)',
                      boxShadow: '0 20px 60px rgba(92,184,255,0.06)'
                    }
                  }}
                >
                  {/* Step number badge */}
                  <Box sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: step.gradient,
                    border: '2px solid rgba(92,184,255,0.3)',
                    mx: 'auto',
                    mb: 3,
                    position: 'relative',
                  }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 900,
                        color: step.iconColor,
                        fontSize: '1.5rem',
                      }}
                    >
                      {step.step}
                    </Typography>
                    {/* Small icon in bottom-right corner of badge */}
                    <Box sx={{
                      position: 'absolute',
                      bottom: -4,
                      right: -4,
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      bgcolor: deepNavy,
                      border: '2px solid rgba(92,184,255,0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: step.iconColor,
                      fontSize: '0.85rem',
                    }}>
                      {React.cloneElement(step.icon, { fontSize: 'small' } as any)}
                    </Box>
                  </Box>

                  <Typography variant="h6" sx={{ fontWeight: 800, color: 'white', mb: 1.5, letterSpacing: '-0.01em' }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', lineHeight: 1.8 }}>
                    {step.description}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>

          {/* CTA Button */}
          <Box sx={{
            textAlign: 'center',
            mt: { xs: 5, md: 6 },
            animation: `${slideUp} 0.6s ease-out 0.5s both`
          }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/signup')}
              endIcon={<ArrowForward />}
              sx={{
                px: 5,
                py: 1.6,
                borderRadius: '14px',
                bgcolor: accent,
                color: deepNavy,
                fontWeight: 800,
                textTransform: 'none',
                fontSize: '0.95rem',
                boxShadow: '0 0 30px rgba(92,184,255,0.25)',
                '&:hover': {
                  bgcolor: accentHover,
                  boxShadow: '0 0 40px rgba(92,184,255,0.4)'
                }
              }}
            >
              立即開始
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ===== CTA SECTION ===== */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box
          sx={{
            borderRadius: '32px',
            overflow: 'hidden',
            position: 'relative',
            background: 'linear-gradient(135deg, rgba(92,184,255,0.08) 0%, rgba(160,216,255,0.06) 50%, rgba(92,184,255,0.04) 100%)',
            border: '1px solid rgba(92,184,255,0.1)'
          }}
        >
          {/* Decorative elements */}
          <Box sx={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(92,184,255,0.04)' }} />
          <Box sx={{ position: 'absolute', bottom: -60, left: -30, width: 160, height: 160, borderRadius: '40%', bgcolor: 'rgba(160,216,255,0.04)', transform: 'rotate(20deg)' }} />

          <Box sx={{ position: 'relative', zIndex: 1, p: { xs: 4, md: 6 }, textAlign: 'center' }}>
            <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center" sx={{ mb: 3 }}>
              <Star sx={{ color: accent, fontSize: 20 }} />
              <Typography variant="overline" sx={{ color: accent, fontWeight: 700, letterSpacing: '0.14em', fontSize: '0.7rem' }}>
                開始使用
              </Typography>
              <Star sx={{ color: accent, fontSize: 20 }} />
            </Stack>
            <Typography variant="h3" sx={{ fontWeight: 900, color: 'white', letterSpacing: '-0.03em', mb: 2 }}>
              想建立自己的體育群組？
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', maxWidth: 600, mx: 'auto', mb: 5, lineHeight: 1.8 }}>
              建立群組後，你可以把指定體育活動公開到首頁，並在同一個流程內管理報名、審批與訊息發送。
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                onClick={() => navigate('/signup')}
                endIcon={<ArrowForward />}
                sx={{
                  px: 5, py: 1.6, borderRadius: '14px',
                  bgcolor: accent, color: deepNavy, fontWeight: 800,
                  textTransform: 'none', fontSize: '0.95rem',
                  boxShadow: `0 0 30px rgba(92,184,255,0.2)`,
                  '&:hover': { bgcolor: '#7ec8ff', boxShadow: `0 0 40px rgba(92,184,255,0.35)` }
                }}
              >
                立即開始
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/login')}
                sx={{
                  px: 5, py: 1.6, borderRadius: '14px',
                  color: 'rgba(255,255,255,0.7)',
                  borderColor: 'rgba(255,255,255,0.12)',
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  '&:hover': {
                    borderColor: accent, color: accent,
                    bgcolor: 'rgba(92,184,255,0.04)'
                  }
                }}
              >
                登入
              </Button>
            </Stack>
          </Box>
        </Box>
      </Container>

      {/* ===== FOOTER ===== */}
      <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.04)', py: 4, mt: 4 }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar src="/logo.png" alt="MatchPoint" sx={{ width: 28, height: 28 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 600 }}>
                MatchPoint
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)' }}>
              &copy; {new Date().getFullYear()} MatchPoint — 體育活動管理平台
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default GuestHome;
